<template>
    <div class="container admin-table-page">
        <!-- HEADER -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- FILTROS PRINCIPALES -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100 flex-wrap">
                <div class="filters-container d-flex align-items-center flex-wrap">
                    <b-form-select 
                        :value="selFormulario" 
                        @change="f_actualiza_formulario($event)" 
                        class="filter-select"
                    >
                        <b-form-select-option value="">{{ labels.selectFormulario }}</b-form-select-option>
                        <option v-for="f in listFormularios" :key="f.id" :value="f.formulario">
                            {{ f.formulario }} - {{ f.descripcion }}
                        </option>
                    </b-form-select>

                    <!-- Crear Formulario -->
                    <b-button class="table-toolbar-add-btn ml-2" style="background-color:#2e507a; border-color:#2e507a;" @click="openCreateFormulario" :disabled="isLoading">
                        <font-awesome-icon :icon="['fas', 'file-alt']" class="mr-1" />
                        Crear Formulario
                    </b-button>

                    <!-- Crear Grupo -->
                    <b-button class="table-toolbar-add-btn ml-2" @click="openCreateGrupo" :disabled="!selFormulario">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                        Crear Grupo
                    </b-button>

                    <!-- Editar Grupo -->
                    <b-button class="table-toolbar-add-btn ml-2" style="background-color:#2e507a; border-color:#2e507a;" @click="openEditGrupo" :disabled="!selFormulario">
                        <font-awesome-icon :icon="['fas', 'pencil-alt']" class="mr-1" />
                        Editar Grupo
                    </b-button>

                    <!-- Consultar -->
                    <b-button class="consult-btn ml-2" @click="consultaFilas" :disabled="!selFormulario || isLoading">
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>
            </div>
        </div>

        <!-- TABLA PRINCIPAL -->
        <div class="admin-table-block mt-4">
            <!-- ESTADO INICIAL -->
            <div v-if="!showTabla && !isLoading">
                <CorporateDataTable
                    :items="[]" :fields="[]" :labels="tableLabels" :loading="false"
                    :default-per-page="10" :page-options="[10]" :narrow="false"
                    :table-min-height="400" :table-max-height="500"
                    :show-search="false" :allow-column-toggle="false"
                    :enable-sorting="false" :show-pagination="false" :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon :icon="['fas', 'search']" size="3x" class="text-muted mb-3" style="opacity: 0.3;" />
                            <p class="h4 text-muted">{{ labels.selectFilters }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- LOADING -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>

            <!-- TABLA DE FILAS -->
            <div v-if="showTabla && !isLoading" class="table-wrapper">
                <CorporateDataTable
                    :items="filaTableData" :fields="filaFields" :labels="filaTableLabels"
                    :default-per-page="10" :page-options="[10, 20, 50, 100]" :narrow="false"
                    :table-min-height="400" :table-max-height="500"
                    :show-search="true" :allow-column-toggle="true" :enable-sorting="true"
                    :filter-defs="filaFilterDefs" :toolbar-disabled="false"
                    :show-pagination="true" :show-rows-per-page="true"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button size="sm" class="table-toolbar-add-btn" @click="openCreateFila">
                                <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                                {{ labels.createRow }}
                            </b-button>
                            <b-button size="sm" class="reset-btn-danger ml-1" style="height:31px; padding:0 14px;"
                                @click="borrarTodasFilas" :disabled="filaTableData.length === 0">
                                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                                {{ labels.deleteAll }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(formulacion)="data">
                        <div class="text-left formulacion-cell" :title="data.value">{{ data.value }}</div>
                    </template>

                    <template #cell(accionEdit)="slotProps">
                        <b-button style="background-color:#FF924C; width:30px; height:30px; padding:0; display:flex; align-items:center; justify-content:center; border-radius:4px; margin:0 auto;"
                            @click.stop="editFila(slotProps.item)">
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs" />
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="{ item }">
                        <b-button class="reset-btn-danger" style="width:30px; height:30px; padding:0; display:flex; align-items:center; justify-content:center; border-radius:4px; margin:0 auto;"
                            @click.stop="deleteFila(item)">
                            <font-awesome-icon :icon="['fas', 'trash']" size="xs" />
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon :icon="['fas', 'table']" class="empty-table-icon" />
                            <p class="text-muted mt-2">{{ labels.noRows }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>
        </div>

        <!-- MODAL CREAR GRUPO -->
        <b-modal :model-value="showModGrupo" :no-close-on-backdrop="true" hide-footer centered size="md"
            title="Crear Nuevo Grupo"
            header-class="reset-modal-header" title-class="reset-modal-title" body-class="reset-modal-body"
            @hide="cerrarModalGrupo">
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">Crear Grupo</div>
                    <div class="password-policy-subtitle">Ingresa los datos del nuevo grupo para este formulario.</div>
                </div>
                <div class="uniform-form-row d-flex align-items-center mb-3">
                    <div class="col-md-4 uniform-label-col"><label class="field-label required">Grupo</label></div>
                    <div class="col-md-8"><input v-model.number="grupoFormData.grupo" type="number" min="1" class="form-control form-control-sm form-input" /></div>
                </div>
                <div class="uniform-form-row d-flex align-items-center mb-3">
                    <div class="col-md-4 uniform-label-col"><label class="field-label required">Descripción</label></div>
                    <div class="col-md-8"><input v-model="grupoFormData.descripcion" maxlength="200" class="form-control form-control-sm form-input" placeholder="Ingrese una descripción..." /></div>
                </div>
                <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
                    <b-button class="reset-btn-secondary" @click="cerrarModalGrupo">Cancelar</b-button>
                    <b-button class="reset-btn-primary ml-2" @click="guardarGrupo" :disabled="!grupoFormData.grupo || !grupoFormData.descripcion || isLoading">Guardar</b-button>
                </div>
            </div>
        </b-modal>

        <!-- MODAL EDITAR GRUPO -->
        <b-modal :model-value="showModEditGrupo" :no-close-on-backdrop="true" hide-footer centered size="lg"
            title="Editar Grupo"
            header-class="reset-modal-header" title-class="reset-modal-title" body-class="reset-modal-body"
            @hide="cerrarModalEditGrupo">
            <div class="uniform-modal-body edit-grupo-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">Editar Grupo</div>
                    <div class="password-policy-subtitle">Selecciona un grupo para editarlo, eliminarlo, o eliminar un código del formulario.</div>
                </div>

                <div class="uniform-form-row d-flex align-items-center mb-3">
                    <div class="col-md-4 uniform-label-col"><label class="field-label required">Grupo</label></div>
                    <div class="col-md-8">
                        <b-form-select v-model.number="editGrupoFormData.id" @change="onSelectGrupoToEdit" class="form-control form-control-sm form-input">
                            <b-form-select-option :value="null">Seleccione...</b-form-select-option>
                            <option v-for="g in listGrupos" :key="g.id" :value="g.id">{{ g.grupo }} - {{ g.descripcion }}</option>
                        </b-form-select>
                    </div>
                </div>

                <template v-if="editGrupoFormData.id">
                    <div class="uniform-form-row d-flex align-items-center mb-3">
                        <div class="col-md-4 uniform-label-col"><label class="field-label required">Nuevo número</label></div>
                        <div class="col-md-8"><input v-model.number="editGrupoFormData.grupo" type="number" min="1" class="form-control form-control-sm form-input" /></div>
                    </div>
                    <div class="uniform-form-row d-flex align-items-center mb-3">
                        <div class="col-md-4 uniform-label-col"><label class="field-label required">Descripción</label></div>
                        <div class="col-md-8"><input v-model="editGrupoFormData.descripcion" maxlength="200" class="form-control form-control-sm form-input" placeholder="Ingrese una descripción..." /></div>
                    </div>

                    <div class="danger-zone mt-4">
                        <div class="danger-zone-title">
                            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-1" />
                            Zona de acciones destructivas
                        </div>
                        <div class="danger-zone-row d-flex align-items-center justify-content-between">
                            <div>
                                <div class="danger-zone-label">Eliminar este grupo</div>
                                <div class="danger-zone-hint">Se eliminarán también sus filas asociadas.</div>
                            </div>
                            <b-button class="reset-btn-danger" size="sm" @click="confirmarDeleteGrupoDesdeEdit">
                                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                                Eliminar grupo
                            </b-button>
                        </div>
                    </div>
                </template>

                <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
                    <b-button class="reset-btn-secondary" @click="cerrarModalEditGrupo">Cerrar</b-button>
                    <b-button class="reset-btn-primary ml-2" @click="guardarEditGrupo" :disabled="!editGrupoFormData.id || !editGrupoFormData.grupo || !editGrupoFormData.descripcion || isLoading">Guardar cambios</b-button>
                </div>
            </div>
        </b-modal>

        <!-- MODAL CONFIRMAR ELIMINAR GRUPO -->
        <b-modal :model-value="showConfirmDeleteGrupo" :no-close-on-backdrop="true" hide-footer centered size="sm"
            title="Eliminar Grupo"
            header-class="reset-modal-header" title-class="reset-modal-title" body-class="reset-modal-body"
            @hide="cerrarConfirmDeleteGrupo">
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">¿Eliminar este grupo?</div>
                    <div class="password-policy-subtitle">
                        Se eliminará el grupo <strong>{{ editGrupoFormData.grupo }} - {{ editGrupoFormData.descripcion }}</strong>
                        y todas sus filas asociadas. Esta acción no se puede deshacer.
                    </div>
                </div>
                <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
                    <b-button class="reset-btn-secondary" @click="cerrarConfirmDeleteGrupo">Cancelar</b-button>
                    <b-button class="reset-btn-primary ml-2" style="background-color:#cd2838; border-color:#cd2838;"
                        @click="executeDeleteGrupoDesdeEdit" :disabled="isLoading">
                        Eliminar
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- MODAL CREAR/EDITAR FORMULARIO -->
        <b-modal :model-value="showModFormulario" :no-close-on-backdrop="true" hide-footer centered size="md"
            :title="isEditingFormulario ? labels.editFormulario : labels.createFormulario"
            header-class="reset-modal-header" title-class="reset-modal-title" body-class="reset-modal-body"
            @hide="cerrarModalFormulario">
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">{{ isEditingFormulario ? labels.editFormulario : labels.createFormulario }}</div>
                    <div class="password-policy-subtitle">{{ isEditingFormulario ? 'Modifica los datos del formulario.' : 'Ingresa los datos del nuevo formulario.' }}</div>
                </div>

                <div class="uniform-form-row d-flex align-items-center mb-3">
                    <div class="col-md-4 uniform-label-col">
                        <label class="field-label required">{{ labels.codFormulario }}</label>
                    </div>
                    <div class="col-md-8">
                        <input v-model.number="formularioFormData.formulario" type="number" min="1" class="form-control form-control-sm form-input"
                            :class="{ 'is-invalid': !formularioFormData.formulario && touchedFormulario }" @blur="touchedFormulario = true" />
                        <div v-if="!formularioFormData.formulario && touchedFormulario" class="invalid-feedback">{{ labels.requiredField }}</div>
                    </div>
                </div>

                <div class="uniform-form-row d-flex align-items-center mb-3">
                    <div class="col-md-4 uniform-label-col">
                        <label class="field-label">{{ labels.description }}</label>
                    </div>
                    <div class="col-md-8">
                        <input v-model="formularioFormData.descripcion" maxlength="200" class="form-control form-control-sm form-input"
                            placeholder="Ingrese una descripción..." />
                    </div>
                </div>

                <!-- NUEVO: mapeo al reporte origen (no toca BD) -->
                <div class="uniform-form-row d-flex align-items-center mb-3">
                    <div class="col-md-4 uniform-label-col">
                        <label class="field-label">Reporte origen</label>
                    </div>
                    <div class="col-md-8">
                        <b-form-select v-model.number="formularioFormData.reporte" class="form-control form-control-sm form-input">
                            <b-form-select-option :value="null">— Por defecto (10) —</b-form-select-option>
                            <b-form-select-option :value="10">10 - VALIDADOR INGRESO</b-form-select-option>
                        </b-form-select>
                        <small class="text-muted">Los códigos se tomarán de este reporte (tbl_reporte_ingresos_cuentas).</small>
                    </div>
                </div>

                <template v-if="!isEditingFormulario">
                    <div class="uniform-form-row d-flex align-items-center mb-3">
                        <div class="col-md-4 uniform-label-col">
                            <label class="field-label">Tomar como referencia</label>
                        </div>
                        <div class="col-md-8">
                            <b-form-select v-model.number="formularioFormData.referenciaFormulario" class="form-control form-control-sm form-input">
                                <b-form-select-option :value="null">— Ninguno (crear vacío) —</b-form-select-option>
                                <option v-for="f in listFormularios" :key="f.id" :value="f.formulario">
                                    {{ f.formulario }} - {{ f.descripcion }}
                                </option>
                            </b-form-select>
                        </div>
                    </div>
                </template>

                <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
                    <b-button class="reset-btn-secondary" @click="cerrarModalFormulario">{{ labels.cancel }}</b-button>
                    <b-button class="reset-btn-primary ml-2" @click="guardarFormulario" :disabled="!formularioFormData.formulario || isLoading">
                        {{ labels.save }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- MODAL CREAR/EDITAR FILA -->
        <b-modal :model-value="showModFila" :no-close-on-backdrop="true" hide-footer centered size="xl"
            dialog-class="modal-asocia-menu-wide"
            :title="isEditingFila ? labels.editRow : labels.createRow"
            header-class="reset-modal-header" title-class="reset-modal-title" body-class="reset-modal-body"
            class="reset-password-dialog modal-fixed-height"
            @hide="cerrarModalFila">
            
            <div class="uniform-modal-body uniform-modal-body--wide">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">{{ isEditingFila ? labels.editRow : labels.createRow }}</div>
                    <div class="password-policy-subtitle">
                        {{ isEditingFila ? 'Modifica los datos de la fila.' : 'Ingresa los datos de la nueva fila.' }}
                        Selecciona el tipo de formulación en la barra lateral y configura sus parámetros.
                    </div>
                </div>

                <div class="menu-association-layout fila-layout">
                    <!-- SIDEBAR -->
                    <div class="menu-sidebar">
                        <div class="menu-sidebar-title">Tipo de formulación</div>
                        <div class="menu-sidebar-list">
                            <b-button v-for="t in tiposFormulacion" :key="t.value"
                                class="menu-sidebar-item"
                                :class="{ active: filaFormData.tipoFormulacion === t.value }"
                                @click="seleccionarTipo(t.value)">
                                {{ t.label }}
                            </b-button>
                        </div>
                    </div>

                    <!-- CONTENIDO -->
                    <div class="menu-content">
                        <div class="menu-content-title">
                            {{ tipoSeleccionadoLabel || 'Selecciona un tipo de formulación' }}
                        </div>

                        <div class="fila-content-scroll">
                            <!-- Datos básicos -->
                            <div class="fila-section">
                                <div class="fila-field">
                                    <label class="field-label required">Grupo</label>
                                    <b-form-select v-model.number="filaFormData.grupo" class="form-input">
                                        <b-form-select-option :value="null">Seleccione...</b-form-select-option>
                                        <option v-for="g in listGrupos" :key="g.id" :value="g.grupo">{{ g.grupo }} - {{ g.descripcion }}</option>
                                    </b-form-select>
                                </div>

                                <div class="fila-field">
                                    <label class="field-label required">{{ labels.fila }}</label>
                                    <b-form-input v-model.number="filaFormData.fila" type="number" min="1" class="form-input"
                                        :class="{ 'is-invalid': !filaFormData.fila && touchedFila }" @blur="touchedFila = true" />
                                    <div v-if="!filaFormData.fila && touchedFila" class="invalid-feedback d-block">{{ labels.requiredField }}</div>
                                </div>

                                <div class="fila-field fila-field--grow">
                                    <label class="field-label">{{ labels.description }}</label>
                                    <b-form-input v-model="filaFormData.descripcion" maxlength="200" class="form-input"
                                        placeholder="Ingrese una descripción..." />
                                </div>

                                <div class="fila-field">
                                    <label class="field-label">¿Fila por CIIU?</label>
                                    <div class="custom-toggle-wrapper custom-toggle-sm">
                                        <input type="checkbox" id="toggle-ciiu" v-model="filaFormData.filaporciiu" />
                                        <label for="toggle-ciiu" class="toggle-label">
                                            <span class="toggle-slider"></span>
                                            <span class="toggle-text">{{ filaFormData.filaporciiu ? 'Sí' : 'No' }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <!-- Sin tipo -->
                            <div class="no-submenus" v-if="!filaFormData.tipoFormulacion">
                                Selecciona un tipo de formulación en la barra lateral
                            </div>

                            <!-- sumacodigos / restemunicipio -->
                            <template v-if="['sumacodigos','restemunicipio'].includes(filaFormData.tipoFormulacion)">
                                <div class="fila-field mb-2">
                                    <label class="field-label">Seleccione los códigos a utilizar</label>
                                    <b-form-input v-model="critCodigo" class="form-input mb-2" placeholder="Buscar código..."
                                        @input="filtraCodigos" />
                                    <div class="custom-scroll-table" style="height: 150px; min-height: 150px; max-height: 150px;">
                                        <table class="table table-sm table-hover" style="font-size:13px; margin-bottom:0;">
                                            <thead>
                                                <tr>
                                                    <th style="width:120px;">{{ labels.code }}</th>
                                                    <th>{{ labels.description }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="c in listCodigosFiltrados" :key="c.codigo"
                                                    @click="toggleCodigoSelection(c)"
                                                    :class="{ 'table-active-blue': isCodigoSelected(c) }"
                                                    style="cursor:pointer;">
                                                    <td><strong>{{ c.codigo }}</strong></td>
                                                    <td :title="c.descripcion">{{ c.descripcion }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div class="mt-2" style="font-size:13px; color:#6c757d;">
                                        {{ labels.selected }}: <strong>{{ filaFormData.codigosSeleccionados.length }}</strong>
                                    </div>
                                </div>

                                <div class="fila-field mb-2" v-if="filaFormData.codigosSeleccionados.length > 0">
                                    <label class="field-label">Operación por código</label>
                                    <div class="operand-list">
                                        <div v-for="(cod, idx) in filaFormData.codigosSeleccionados" :key="cod" class="operand-row">
                                            <span class="operand-index">{{ idx + 1 }}</span>
                                            <b-form-select v-model="filaFormData.operadoresCodigos[idx]" style="width:70px;">
                                                <option value="+">+</option>
                                                <option value="-">−</option>
                                                <option value="*">×</option>
                                                <option value="/">÷</option>
                                            </b-form-select>
                                            <span class="operand-code">Código {{ cod }}</span>
                                            <b-button size="sm" class="reset-btn-danger" @click="removeCodigoDeLista(idx)">
                                                <font-awesome-icon :icon="['fas', 'trash']" size="xs" />
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- sumafilas -->
                            <template v-if="filaFormData.tipoFormulacion === 'sumafilas'">
                                <div class="fila-field mb-2">
                                    <label class="field-label">Seleccione las filas a operar</label>
                                    <div class="custom-scroll-table" style="height: 180px; min-height: 180px; max-height: 180px;">
                                        <table class="table table-sm table-hover" style="font-size:13px; margin-bottom:0;">
                                            <thead>
                                                <tr>
                                                    <th style="width:100px;">{{ labels.fila }}</th>
                                                    <th>{{ labels.description }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="f in listFilasExistentes" :key="f.id"
                                                    @click="toggleFilaSelection(f)"
                                                    :class="{ 'table-active-blue': isFilaSelected(f) }"
                                                    style="cursor:pointer;">
                                                    <td><strong>{{ f.fila }}</strong></td>
                                                    <td :title="f.descripcion">{{ f.descripcion }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div class="mt-2" style="font-size:13px; color:#6c757d;">
                                        {{ labels.selected }}: <strong>{{ filaFormData.filasSeleccionadas.length }}</strong>
                                    </div>
                                </div>

                                <div class="fila-field mb-2" v-if="filaFormData.filasSeleccionadas.length > 0">
                                    <label class="field-label">Operación por fila</label>
                                    <div class="operand-list">
                                        <div v-for="(fila, idx) in filaFormData.filasSeleccionadas" :key="fila" class="operand-row">
                                            <span class="operand-index">{{ idx + 1 }}</span>
                                            <b-form-select v-model="filaFormData.operadoresFilas[idx]" style="width:70px;">
                                                <option value="+">+</option>
                                                <option value="-">−</option>
                                                <option value="*">×</option>
                                                <option value="/">÷</option>
                                            </b-form-select>
                                            <span class="operand-code">Fila {{ fila }}</span>
                                            <b-button size="sm" class="reset-btn-danger" @click="removeFilaDeLista(idx)">
                                                <font-awesome-icon :icon="['fas', 'trash']" size="xs" />
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- sumamunicipio -->
                            <template v-if="filaFormData.tipoFormulacion === 'sumamunicipio'">
                                <div class="fila-field mb-2">
                                    <label class="field-label">Seleccione los códigos a operar</label>
                                    <b-form-input v-model="critCodigo" class="form-input mb-2" placeholder="Buscar código..."
                                        @input="filtraCodigos" />
                                    <div class="custom-scroll-table" style="height: 180px; min-height: 180px; max-height: 180px;">
                                        <table class="table table-sm table-hover" style="font-size:13px; margin-bottom:0;">
                                            <thead>
                                                <tr>
                                                    <th style="width:120px;">{{ labels.code }}</th>
                                                    <th>{{ labels.description }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="c in listCodigosFiltrados" :key="c.codigo"
                                                    @click="toggleCodigoMunicipioSelection(c)"
                                                    :class="{ 'table-active-blue': isCodigoMunicipioSelected(c) }"
                                                    style="cursor:pointer;">
                                                    <td><strong>{{ c.codigo }}</strong></td>
                                                    <td :title="c.descripcion">{{ c.descripcion }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div class="mt-2" style="font-size:13px; color:#6c757d;">
                                        {{ labels.selected }}: <strong>{{ filaFormData.codigosMunicipioSeleccionados.length }}</strong>
                                    </div>
                                </div>

                                <div class="fila-field mb-2" v-if="filaFormData.codigosMunicipioSeleccionados.length > 0">
                                    <label class="field-label">Operación por código</label>
                                    <div class="operand-list">
                                        <div v-for="(cod, idx) in filaFormData.codigosMunicipioSeleccionados" :key="cod" class="operand-row">
                                            <span class="operand-index">{{ idx + 1 }}</span>
                                            <b-form-select v-model="filaFormData.operadoresCodigosMunicipio[idx]" style="width:70px;">
                                                <option value="+">+</option>
                                                <option value="-">−</option>
                                                <option value="*">×</option>
                                                <option value="/">÷</option>
                                            </b-form-select>
                                            <span class="operand-code">Código {{ cod }}</span>
                                            <b-button size="sm" class="reset-btn-danger" @click="removeCodigoMunicipioDeLista(idx)">
                                                <font-awesome-icon :icon="['fas', 'trash']" size="xs" />
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- sumaporcentajes -->
                            <template v-if="filaFormData.tipoFormulacion === 'sumaporcentajes'">
                                <div class="fila-field mb-2">
                                    <label class="field-label">Agregue los operandos de la expresión</label>
                                    <div class="mb-2 d-flex align-items-center" style="gap: 12px;">
                                        <b-form-select v-model="nuevoOperandoFila" class="form-input" style="max-width:280px;">
                                            <b-form-select-option value="">Seleccione fila...</b-form-select-option>
                                            <option v-for="f in listFilasExistentes" :key="f.id" :value="f.fila">
                                                {{ f.fila }} - {{ f.descripcion }}
                                            </option>
                                        </b-form-select>
                                        <b-button size="sm" class="btn-native-add" @click="addOperandoPorcentaje"
                                            :disabled="!nuevoOperandoFila">
                                            <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" /> Agregar
                                        </b-button>
                                    </div>
                                </div>

                                <div class="fila-field mb-2" v-if="filaFormData.operandosPorcentaje.length > 0">
                                    <label class="field-label">Expresión configurada</label>
                                    <div class="operand-list">
                                        <div v-for="(op, idx) in filaFormData.operandosPorcentaje" :key="idx" class="operand-row">
                                            <span class="operand-index">{{ idx + 1 }}</span>
                                            <b-form-select v-model="op.operador" style="width:70px;">
                                                <option value="+">+</option>
                                                <option value="-">−</option>
                                                <option value="*">×</option>
                                                <option value="/">÷</option>
                                            </b-form-select>
                                            <span class="operand-code">Fila {{ op.fila }}</span>
                                            <div class="custom-toggle-wrapper custom-toggle-sm ml-2">
                                                <input :id="'toggle-porc-' + idx" type="checkbox" v-model="op.conPorcentaje" />
                                                <label :for="'toggle-porc-' + idx" class="toggle-label">
                                                    <span class="toggle-slider"></span>
                                                    <span class="toggle-text">%</span>
                                                </label>
                                            </div>
                                            <b-button size="sm" class="reset-btn-danger ml-2" @click="removeOperandoPorcentaje(idx)">
                                                <font-awesome-icon :icon="['fas', 'trash']" size="xs" />
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- sumacompleja -->
                            <template v-if="filaFormData.tipoFormulacion === 'sumacompleja'">
                                <div class="fila-field mb-3">
                                    <label class="field-label">Comparación</label>
                                    <b-form-select v-model="filaFormData.comparacion" class="form-input">
                                        <b-form-select-option value="">{{ labels.select }}</b-form-select-option>
                                        <b-form-select-option value="<0">Menor que cero (&lt;0)</b-form-select-option>
                                        <b-form-select-option value=">0">Mayor que cero (&gt;0)</b-form-select-option>
                                    </b-form-select>
                                </div>

                                <div class="fila-field mb-3">
                                    <label class="field-label">Términos</label>
                                    <div v-for="(term, idx) in filaFormData.terminosComplejos" :key="idx"
                                        class="d-flex align-items-center mb-2" style="gap:8px;">
                                        <b-form-select v-model="term.signo" style="width:80px;">
                                            <option value="+">+</option>
                                            <option value="-">-</option>
                                        </b-form-select>
                                        <b-form-select v-model="term.fila" style="flex:1;">
                                            <b-form-select-option value="">Seleccione fila</b-form-select-option>
                                            <option v-for="f in listFilasExistentes" :key="f.id" :value="f.fila">
                                                {{ f.fila }} - {{ f.descripcion }}
                                            </option>
                                        </b-form-select>
                                        <b-button size="sm" class="reset-btn-danger" @click="removeTerminoComplejo(idx)">
                                            <font-awesome-icon :icon="['fas', 'trash']" size="xs" />
                                        </b-button>
                                    </div>
                                    <b-button size="sm" class="btn-native-add mt-1" @click="addTerminoComplejo">
                                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" /> Agregar término
                                    </b-button>
                                </div>
                            </template>

                            <!-- manual -->
                            <template v-if="filaFormData.tipoFormulacion === 'manual'">
                                <b-alert show variant="info" class="mt-2 mb-0">
                                    El valor de esta fila se ingresará de forma manual.
                                </b-alert>
                            </template>

                            <!-- valorxtarifa -->
                            <template v-if="filaFormData.tipoFormulacion === 'valorxtarifa'">
                                <b-alert show variant="info" class="mt-2 mb-0">
                                    Esta fila calculará el valor multiplicando la tarifa por el CIIU seleccionado.
                                    La formulación se guardará como <strong>valor*tarifa</strong> y no requiere
                                    configuración adicional.
                                </b-alert>
                            </template>
                        </div>

                        <div class="fila-preview">
                            <div class="fila-preview-label">{{ labels.preview }}</div>
                            <div class="fila-preview-code">{{ previewDescriptivo || '—' }}</div>
                        </div>
                    </div>
                </div>

                <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
                    <b-button class="mr-2 reset-btn-secondary" @click="cerrarModalFila">{{ labels.cancel }}</b-button>
                    <b-button class="reset-btn-primary" @click="guardarFila" :disabled="!isFilaValid || isLoading">
                        {{ labels.save }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- MODAL CONFIRMACIÓN ELIMINAR FILA -->
        <b-modal :model-value="showConfirmDeleteModal" :no-close-on-backdrop="true" hide-footer centered size="md"
            :title="labels.confirmDelete" header-class="reset-modal-header" title-class="reset-modal-title"
            body-class="reset-modal-body" @hide="closeConfirmDeleteModal">
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">{{ labels.areYouSure }}</div>
                    <div class="password-policy-subtitle">{{ labels.confirmDeleteText }}</div>
                </div>
                <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
                    <b-button class="reset-btn-secondary" @click="closeConfirmDeleteModal">{{ labels.cancel }}</b-button>
                    <b-button class="reset-btn-primary ml-2" @click="executeDelete" style="background-color:#cd2838; border-color:#cd2838;">
                        {{ labels.confirm }}
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import ReporteLiquidacionService from '../../services/ReporteLiquidacion'
import CorporateDataTable from '../utils/CorporateDataTable.vue'

// ============================================================
// MAPEO FORMULARIO -> REPORTE (sin tocar BD)
// ============================================================
// El reporte vive en tbl_reporte_ingresos_cuentas.reporte.
// Por ahora solo existe el reporte 10 (VALIDADOR INGRESO).
// Cualquier formulario que no esté mapeado usa DEFAULT_REPORTE.
const DEFAULT_REPORTE = 10
const REPORTE_POR_FORMULARIO = {
    // 20: 10,  // ejemplo: si el formulario 20 debe usar el reporte 10
    // 30: 10,
}

export default {
    name: 'ReporteLiquidacion',
    components: { CorporateDataTable },

    data: () => ({
        // ===== Catálogos =====
        listFormularios: [],
        listGrupos: [],
        listFilasExistentes: [],
        listCodigos: [],
        listCodigosFiltrados: [],

        // ===== Selecciones =====
        selFormulario: '',
        critCodigo: '',
        reporteActual: DEFAULT_REPORTE, // reporte resuelto para el formulario actual

        // ===== Estado =====
        isLoading: false,
        showTabla: false,

        // ===== Formulario =====
        showModFormulario: false,
        isEditingFormulario: false,
        touchedFormulario: false,
        formularioFormData: {
            id: null,
            formulario: null,
            descripcion: '',
            referenciaFormulario: null,
            reporte: null, // mapeo opcional (no se persiste en BD)
        },

        // ===== Grupo =====
        showModGrupo: false,
        grupoFormData: { grupo: null, descripcion: '' },

        // ===== Editar Grupo =====
        showModEditGrupo: false,
        editGrupoFormData: { id: null, grupo: null, descripcion: '' },
        showConfirmDeleteGrupo: false,

        // ===== Fila =====
        showModFila: false,
        isEditingFila: false,
        touchedFila: false,
        nuevoOperandoFila: '',
        filaFormData: {
            id: null,
            grupo: null,
            fila: null,
            descripcion: '',
            filaporciiu: false,
            tipoFormulacion: '',
            codigosSeleccionados: [],
            operadoresCodigos: [],
            filasSeleccionadas: [],
            operadoresFilas: [],
            codigosMunicipioSeleccionados: [],
            operadoresCodigosMunicipio: [],
            operandosPorcentaje: [],
            multifila: false,
            conPorcentaje: false,
            comparacion: '',
            terminosComplejos: [],
            filaBasePorcentaje: '',
            filaSumaPorcentaje: ''
        },

        // ===== Confirmación delete fila =====
        showConfirmDeleteModal: false,
        deleteId: null,
        deleteTarget: '',
        isDeleteAll: false
    }),

    computed: {
        labels() {
            const es = this.$store.state._language == "es-ES"
            return {
                title: es ? 'Reporte Liquidación' : 'Liquidation Report',
                selectFormulario: es ? 'Seleccione Formulario' : 'Select Form',
                consultar: es ? 'Consultar' : 'Consult',
                selectFilters: es ? 'Seleccione un formulario para consultar' : 'Select a form to consult',
                loading: es ? 'Cargando...' : 'Loading...',
                createRow: es ? 'Crear Fila' : 'Create Row',
                createGrupo: es ? 'Crear Grupo' : 'Create Group',
                createFormulario: es ? 'Crear Formulario' : 'Create Form',
                editFormulario: es ? 'Editar Formulario' : 'Edit Form',
                editGrupo: es ? 'Editar Grupo' : 'Edit Group',
                deleteAll: es ? 'Eliminar Todo' : 'Delete All',
                edit: es ? 'Editar' : 'Edit',
                delete: es ? 'Eliminar' : 'Delete',
                fila: es ? 'Fila' : 'Row',
                grupo: es ? 'Grupo' : 'Group',
                codFormulario: es ? 'Código Formulario' : 'Form Code',
                descripcion: es ? 'Descripción' : 'Description',
                formulacion: es ? 'Formulación' : 'Formulation',
                description: es ? 'Descripción' : 'Description',
                noRows: es ? 'No hay filas disponibles' : 'No rows available',
                confirmDelete: es ? 'Confirmar Eliminación' : 'Confirm Delete',
                areYouSure: es ? '¿Está seguro?' : 'Are you sure?',
                confirmDeleteText: es ? '¿Está seguro que desea eliminar este registro?' : 'Are you sure you want to delete this record?',
                cancel: es ? 'Cancelar' : 'Cancel',
                confirm: es ? 'Confirmar' : 'Confirm',
                save: es ? 'Guardar' : 'Save',
                requiredField: es ? 'Este campo es requerido' : 'This field is required',
                noMatches: es ? 'No se encontraron resultados' : 'No results found',
                searchPlaceholder: es ? 'Buscar en la tabla...' : 'Search in table...',
                rowsPerPage: es ? 'Filas por página:' : 'Rows per page:',
                noSort: es ? 'Sin orden' : 'No sorting',
                sortAsc: es ? 'A-Z / Menor a mayor' : 'A-Z / Low to high',
                sortDesc: es ? 'Z-A / Mayor a menor' : 'Z-A / High to low',
                columns: es ? 'Columnas' : 'Columns',
                showAllColumns: es ? 'Mostrar todas' : 'Show all',
                filters: es ? 'Filtros' : 'Filters',
                clearFilters: es ? 'Limpiar filtros' : 'Clear filters',
                select: es ? 'Seleccione...' : 'Select...',
                codigos: es ? 'Códigos' : 'Codes',
                code: es ? 'Código' : 'Code',
                selected: es ? 'Seleccionados' : 'Selected',
                filas: es ? 'Filas' : 'Rows',
                multifila: es ? 'Múltiples filas' : 'Multiple rows',
                conPorcentaje: es ? 'Con porcentaje' : 'With percentage',
                preview: es ? 'Vista previa de la formulación' : 'Formulation preview',
                editRow: es ? 'Editar Fila' : 'Edit Row'
            }
        },

        tiposFormulacion() {
            const es = this.$store.state._language == "es-ES"
            return [
                { value: 'sumacodigos', label: es ? 'Información municipios' : 'Sum codes' },
                { value: 'restemunicipio', label: es ? 'Información otros municipios' : 'Subtract codes' },
                { value: 'sumafilas', label: es ? 'Opreaciones filas anteriores' : 'Operate rows' },
                { value: 'sumamunicipio', label: es ? 'Información municipio unico' : 'Operate balance codes' },
                { value: 'sumaporcentajes', label: es ? 'Operar filas con porcentaje' : 'Operate rows with percentage' },
                { value: 'sumacompleja', label: es ? 'Suma compleja condicional' : 'Conditional complex sum' },
                { value: 'manual', label: es ? 'Valor manual' : 'Manual value' },
                { value: 'valorxtarifa', label: es ? 'Valor por tarifa (CIIU)' : 'Value by tariff (CIIU)' }
            ]
        },

        tipoSeleccionadoLabel() {
            const t = this.tiposFormulacion.find(x => x.value === this.filaFormData.tipoFormulacion)
            return t ? t.label : ''
        },

        filaTableData() {
            return (this.listFilasExistentes || []).map(item => ({
                id: item.id,
                grupo: item.nombre_grupo || item.grupo,
                filaporciiu: item.filaporciiu,
                fila: item.fila,
                descripcion: item.descripcion,
                formulacion: item.formulacion
            }))
        },

        filaFields() {
            return [
                { key: 'fila', label: this.labels.fila, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', style: 'width: 80px;' },
                { key: 'descripcion', label: this.labels.descripcion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'grupo', label: this.labels.grupo, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', style: 'width: 200px;' },
                { key: 'filaporciiu', label: 'CIIU', sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', style: 'width: 80px;' },
                { key: 'formulacion', label: this.labels.formulacion, sortable: false, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, style: 'width: 70px;' },
                { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, style: 'width: 70px;' }
            ]
        },

        filaFilterDefs() {
            return [
                { key: 'grupo', type: 'text', placeholder: 'Filtrar grupo...' },
                { key: 'fila', type: 'text', placeholder: 'Filtrar fila...' },
                { key: 'descripcion', type: 'text', placeholder: 'Filtrar descripción...' }
            ]
        },

        tableLabels() {
            return {
                noRow: this.labels.noRows,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },

        filaTableLabels() {
            return { ...this.tableLabels, noRow: this.labels.noRows }
        },

        formulacionBd() {
            const d = this.filaFormData
            switch (d.tipoFormulacion) {
                case 'sumacodigos':
                case 'restemunicipio': {
                    if (d.codigosSeleccionados.length === 0) return ''
                    const expr = d.codigosSeleccionados.map((cod, i) => {
                        const op = d.operadoresCodigos[i] || '+'
                        return i === 0 ? String(cod) : `${op}${cod}`
                    }).join('')
                    return `${d.tipoFormulacion}(${expr})`
                }
                case 'sumafilas': {
                    if (d.filasSeleccionadas.length === 0) return ''
                    const expr = d.filasSeleccionadas.map((fila, i) => {
                        const op = d.operadoresFilas[i] || '+'
                        return i === 0 ? String(fila) : `${op}${fila}`
                    }).join('')
                    return `sumafilas(${expr})`
                }
                case 'sumamunicipio': {
                    if (d.codigosMunicipioSeleccionados.length === 0) return ''
                    const expr = d.codigosMunicipioSeleccionados.map((cod, i) => {
                        const op = d.operadoresCodigosMunicipio[i] || '+'
                        return i === 0 ? String(cod) : `${op}${cod}`
                    }).join('')
                    return `sumamunicipio(${expr})`
                }
                case 'sumaporcentajes': {
                    if (d.operandosPorcentaje.length === 0) return ''
                    const expr = d.operandosPorcentaje.map((op, i) => {
                        const base = `${op.fila}${op.conPorcentaje ? '*porcentaje' : ''}`
                        return i === 0 ? base : `${op.operador}${base}`
                    }).join('')
                    return `sumaporcentajes(${expr})`
                }
                case 'sumacompleja': {
                    if (d.terminosComplejos.length === 0) return ''
                    let expr = ''
                    d.terminosComplejos.forEach((t, i) => {
                        if (i === 0) expr += t.signo === '-' ? '-' : ''
                        else expr += t.signo
                        expr += t.fila
                    })
                    return `sumafilas(${expr})${d.comparacion}`
                }
                case 'manual':
                    return 'manual'
                case 'valorxtarifa':
                    return 'valor*tarifa'
                default:
                    return ''
            }
        },

        previewDescriptivo() {
            const d = this.filaFormData
            const sym = { '+': '+', '-': '−', '*': '×', '/': '÷' }
            switch (d.tipoFormulacion) {
                case 'sumacodigos': {
                    if (d.codigosSeleccionados.length === 0) return ''
                    const partes = d.codigosSeleccionados.map((cod, i) => {
                        const op = d.operadoresCodigos[i] || '+'
                        const label = `${this.getNombreCodigo(cod)} (${cod})`
                        return i === 0 ? label : `${sym[op] || op} ${label}`
                    })
                    return `Suma de las cuentas de: ${partes.join(' ')}`
                }
                case 'restemunicipio': {
                    if (d.codigosSeleccionados.length === 0) return ''
                    const partes = d.codigosSeleccionados.map((cod, i) => {
                        const op = d.operadoresCodigos[i] || '+'
                        const label = `${this.getNombreCodigo(cod)} (${cod})`
                        return i === 0 ? label : `${sym[op] || op} ${label}`
                    })
                    return `Cuentas del: ${partes.join(' ')} en el resto del país (excluye este municipio)`
                }
                case 'sumafilas': {
                    if (d.filasSeleccionadas.length === 0) return ''
                    const operacion = d.filasSeleccionadas.map((fila, i) => {
                        const op = d.operadoresFilas[i] || '+'
                        return i === 0 
                            ? `Fila (${fila})` 
                            : `${sym[op] || op} Fila (${fila})`
                    }).join(' ')
                    return `Operar los valores de las filas: ${operacion}`
                }
                case 'sumamunicipio': {
                    if (d.codigosMunicipioSeleccionados.length === 0) return ''
                    const partes = d.codigosMunicipioSeleccionados.map((cod, i) => {
                        const op = d.operadoresCodigosMunicipio[i] || '+'
                        const label = `${this.getNombreCodigo(cod)} (${cod})`
                        return i === 0 ? label : `${sym[op] || op} ${label}`
                    })
                    return `En este municipio, cuentas de: ${partes.join(' ')}`
                }
                case 'sumaporcentajes': {
                    if (d.operandosPorcentaje.length === 0) return ''
                    return d.operandosPorcentaje.map((op, i) => {
                        const base = `Fila ${op.fila}${op.conPorcentaje ? ' × tarifa %' : ''}`
                        return i === 0 ? base : `${sym[op.operador] || op.operador} ${base}`
                    }).join(' ')
                }
                case 'sumacompleja': {
                    if (d.terminosComplejos.length === 0) return ''
                    let txt = ''
                    d.terminosComplejos.forEach((t, i) => {
                        if (i === 0) txt += t.signo === '-' ? `− Fila ${t.fila}` : `Fila ${t.fila}`
                        else txt += ` ${sym[t.signo] || t.signo} Fila ${t.fila}`
                    })
                    const comp = d.comparacion === '<0'
                        ? 'solo si el resultado es menor que cero'
                        : d.comparacion === '>0'
                            ? 'solo si el resultado es mayor que cero'
                            : ''
                    return comp ? `${txt} (${comp})` : txt
                }
                case 'manual':
                    return 'Valor ingresado manualmente por el usuario'
                case 'valorxtarifa':
                    return 'Valor calculado multiplicando la tarifa por el CIIU (valor*tarifa)'
                default:
                    return ''
            }
        },

        isFilaValid() {
            if (!this.filaFormData.grupo) return false
            if (!this.filaFormData.fila) return false
            if (!this.filaFormData.tipoFormulacion) return false
            const d = this.filaFormData
            const t = d.tipoFormulacion
            if (['sumacodigos','restemunicipio'].includes(t)) {
                return d.codigosSeleccionados.length > 0
            }
            if (t === 'sumafilas') {
                return d.filasSeleccionadas.length > 0
            }
            if (t === 'sumamunicipio') {
                return d.codigosMunicipioSeleccionados.length > 0
            }
            if (t === 'sumaporcentajes') {
                return d.operandosPorcentaje.length > 0
            }
            if (t === 'sumacompleja') {
                return d.comparacion !== '' &&
                    d.terminosComplejos.length > 0 &&
                    d.terminosComplejos.every(t2 => t2.fila !== '')
            }
            return true
        }
    },

    watch: {
        'filaFormData.conPorcentaje'(val) { if (val) this.filaFormData.multifila = false },
        'filaFormData.multifila'(val) { if (val) this.filaFormData.conPorcentaje = false }
    },

    methods: {
        goBack() { this.$router.back() },

        // ============================================================
        // REPORTE RESUELTO PARA UN FORMULARIO
        // ============================================================
        getReporteDeFormulario(formulario) {
            if (!formulario) return DEFAULT_REPORTE
            const map = REPORTE_POR_FORMULARIO
            return map[formulario] !== undefined ? map[formulario] : DEFAULT_REPORTE
        },

        cargarFormularios() {
            this.isLoading = true
            ReporteLiquidacionService.getAllFormularios()
                .then(res => { this.listFormularios = res.data.formularios || [] })
                .catch(err => { console.error(err); this.$toast.error('Error al cargar formularios') })
                .finally(() => { this.isLoading = false })
        },

        f_actualiza_formulario(valorSeleccionado) {
            this.selFormulario = valorSeleccionado;
            this.listGrupos = [];
            this.listFilasExistentes = [];
            this.showTabla = false;

            // Resolver reporte para este formulario
            this.reporteActual = this.getReporteDeFormulario(this.selFormulario)

            if (this.selFormulario) {
                this.cargarGrupos();
            }
        },

        cargarGrupos() {
            ReporteLiquidacionService.getGruposByFormulario(this.selFormulario)
                .then(res => { this.listGrupos = res.data.grupos || [] })
                .catch(err => console.error(err))
        },

        cargarCodigos() {
            // 👇 AQUÍ el cambio clave: usamos el reporte resuelto, no selFormulario
            const reporte = this.getReporteDeFormulario(this.selFormulario)
            ReporteLiquidacionService.getCodigosByReporte(reporte)
                .then(res => {
                    this.listCodigos = res.data.codigos || []
                    this.listCodigosFiltrados = [...this.listCodigos]
                })
                .catch(err => console.error(err))
        },

        consultaFilas() {
            if (!this.selFormulario) { this.$toast.error('Seleccione un formulario'); return }
            this.isLoading = true
            this.listFilasExistentes = []
            
            ReporteLiquidacionService.getFilasByFormulario(this.selFormulario)
                .then(res => {
                    this.listFilasExistentes = res.data.filas || []
                    this.showTabla = true
                })
                .catch(err => { console.error(err); this.$toast.error('Error al cargar filas') })
                .finally(() => { this.isLoading = false })
        },

        // ============================================================
        // FORMULARIO CRUD
        // ============================================================
        openCreateFormulario() {
            this.isEditingFormulario = false
            this.touchedFormulario = false
            this.formularioFormData = {
                id: null,
                formulario: null,
                descripcion: '',
                referenciaFormulario: null,
                reporte: null,
            }
            this.showModFormulario = true
        },

        cerrarModalFormulario() {
            this.showModFormulario = false
            this.formularioFormData = {
                id: null,
                formulario: null,
                descripcion: '',
                referenciaFormulario: null,
                reporte: null,
            }
        },

        async guardarFormulario() {
            if (!this.formularioFormData.formulario) {
                this.touchedFormulario = true
                this.$toast.warning('El código de formulario es obligatorio')
                return
            }
            const userId = this.$store.state.userId || localStorage.getItem('userId')

            const existe = this.listFormularios.some(
                f => Number(f.formulario) === Number(this.formularioFormData.formulario)
            )
            if (!this.isEditingFormulario && existe) {
                this.$toast.error('Ya existe un formulario con ese código')
                return
            }

            this.isLoading = true
            try {
                if (this.isEditingFormulario) {
                    const payload = {
                        id: this.formularioFormData.id,
                        formulario: this.formularioFormData.formulario,
                        descripcion: this.formularioFormData.descripcion,
                        idUser: parseInt(userId)
                    }
                    await ReporteLiquidacionService.updateFormulario(payload)
                    this.$toast.success('Formulario actualizado')
                } else {
                        const tieneReferencia = !!this.formularioFormData.referenciaFormulario;
                        if (tieneReferencia) {
                            this.$toast.info(`Copiando contenido del formulario ${this.formularioFormData.referenciaFormulario}...`)
                        }

                        const payload = {
                            formulario: this.formularioFormData.formulario,
                            descripcion: this.formularioFormData.descripcion,
                            idUser: parseInt(userId),
                            referenciaFormulario: this.formularioFormData.referenciaFormulario || null,
                            copiarGrupos: tieneReferencia
                        }
                    await ReporteLiquidacionService.createFormulario(payload)
                    this.$toast.success('Formulario creado')
                }
                this.cerrarModalFormulario()
                this.cargarFormularios()
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Error al guardar formulario')
            } finally {
                this.isLoading = false
            }
        },

        // ==== GRUPO CRUD ====
        openCreateGrupo() {
            if (!this.selFormulario) { this.$toast.error('Primero seleccione un formulario'); return }
            this.grupoFormData = { grupo: null, descripcion: '' }
            this.showModGrupo = true
        },
        cerrarModalGrupo() { this.showModGrupo = false },
        async guardarGrupo() {
            if (!this.grupoFormData.grupo || !this.grupoFormData.descripcion) { this.$toast.warning('Complete los campos'); return }
            
            const existe = this.listGrupos.some(g => g.grupo === this.grupoFormData.grupo);
            if (existe) { this.$toast.error('Ya existe un grupo con este número.'); return; }

            const userId = this.$store.state.userId || localStorage.getItem('userId')
            this.isLoading = true
            try {
                const payload = {
                    formulario: parseInt(this.selFormulario),
                    grupo: this.grupoFormData.grupo,
                    descripcion: this.grupoFormData.descripcion,
                    idUser: parseInt(userId)
                }
                await ReporteLiquidacionService.createGrupo(payload)
                this.$toast.success('Grupo creado')
                this.cerrarModalGrupo()
                this.cargarGrupos()
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Error al guardar')
            } finally { this.isLoading = false }
        },

        // ==== EDITAR GRUPO ====
        openEditGrupo() {
            if (!this.selFormulario) { this.$toast.error('Seleccione un formulario'); return }
            this.editGrupoFormData = { id: null, grupo: null, descripcion: '' }
            this.cargarGrupos()
            this.cargarCodigos()
            this.showModEditGrupo = true
        },
        cerrarModalEditGrupo() {
            this.showModEditGrupo = false
            this.editGrupoFormData = { id: null, grupo: null, descripcion: '' }
        },
        onSelectGrupoToEdit(id) {
            const g = this.listGrupos.find(x => x.id === id)
            if (g) {
                this.editGrupoFormData.grupo = g.grupo
                this.editGrupoFormData.descripcion = g.descripcion
            }
        },
        async guardarEditGrupo() {
            const f = this.editGrupoFormData
            if (!f.id || !f.grupo || !f.descripcion) { this.$toast.warning('Complete los campos'); return }

            const duplicado = this.listGrupos.some(
                g => g.id !== f.id && Number(g.grupo) === Number(f.grupo)
            )
            if (duplicado) { this.$toast.error('Ya existe otro grupo con ese número'); return }

            const userId = this.$store.state.userId || localStorage.getItem('userId')
            this.isLoading = true
            try {
                const payload = {
                    id: f.id,
                    grupo: f.grupo,
                    descripcion: f.descripcion,
                    idUser: parseInt(userId)
                }
                await ReporteLiquidacionService.updateGrupo(payload)
                this.$toast.success('Grupo actualizado')
                this.cargarGrupos()
                if (this.showTabla) this.consultaFilas()
                const sel = this.listGrupos.find(x => x.id === f.id)
                if (sel) {
                    this.editGrupoFormData.grupo = sel.grupo
                    this.editGrupoFormData.descripcion = sel.descripcion
                }
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Error al actualizar')
            } finally { this.isLoading = false }
        },

        confirmarDeleteGrupoDesdeEdit() {
            if (!this.editGrupoFormData.id) return
            this.showConfirmDeleteGrupo = true
        },
        cerrarConfirmDeleteGrupo() {
            this.showConfirmDeleteGrupo = false
        },
        async executeDeleteGrupoDesdeEdit() {
            if (!this.editGrupoFormData.id) return
            this.isLoading = true
            try {
                await ReporteLiquidacionService.deleteGrupo(this.editGrupoFormData.id)
                this.$toast.success('Grupo eliminado')
                this.cerrarConfirmDeleteGrupo()
                this.cerrarModalEditGrupo()
                this.cargarGrupos()
                if (this.showTabla) this.consultaFilas()
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Error al eliminar grupo')
            } finally { this.isLoading = false }
        },

        // ==== FILA CRUD ====
        openCreateFila() {
            this.isEditingFila = false
            this.touchedFila = false
            this.resetFilaFormData()
            this.cargarDatosAuxiliares()
            this.showModFila = true
        },

        resetFilaFormData() {
            this.filaFormData = {
                id: null, grupo: null, fila: null, descripcion: '', filaporciiu: false,
                tipoFormulacion: '',
                codigosSeleccionados: [], operadoresCodigos: [],
                filasSeleccionadas: [], operadoresFilas: [],
                codigosMunicipioSeleccionados: [], operadoresCodigosMunicipio: [],
                operandosPorcentaje: [],
                multifila: false, conPorcentaje: false, comparacion: '',
                terminosComplejos: [], filaBasePorcentaje: '', filaSumaPorcentaje: ''
            }
            this.nuevoOperandoFila = ''
            this.critCodigo = ''
            this.listCodigosFiltrados = []
        },

        editFila(item) {
            this.isEditingFila = true
            this.touchedFila = false
            const original = this.listFilasExistentes.find(f => f.id === item.id)
            if (!original) { this.$toast.error('No se encontró la fila'); return }

            const parsed = this.parseFormulacion(original.formulacion)
            this.filaFormData = {
                id: original.id,
                grupo: original.grupo,
                fila: original.fila,
                descripcion: original.descripcion || '',
                filaporciiu: original.filaporciiu === 1,
                tipoFormulacion: parsed.tipo,
                codigosSeleccionados: parsed.codigos || [],
                operadoresCodigos: parsed.operadoresCodigos || [],
                filasSeleccionadas: parsed.filas || [],
                operadoresFilas: parsed.operadoresFilas || [],
                codigosMunicipioSeleccionados: parsed.codigosMunicipio || [],
                operadoresCodigosMunicipio: parsed.operadoresCodigosMunicipio || [],
                operandosPorcentaje: parsed.operandosPorcentaje || [],
                multifila: false,
                conPorcentaje: false,
                comparacion: parsed.comparacion || '',
                terminosComplejos: parsed.terminos || [],
                filaBasePorcentaje: '',
                filaSumaPorcentaje: ''
            }
            this.cargarDatosAuxiliares()
            this.showModFila = true
        },

        cargarDatosAuxiliares() {
            // 👇 CAMBIO CLAVE: usar el reporte resuelto, no selFormulario
            const reporte = this.getReporteDeFormulario(this.selFormulario)
            ReporteLiquidacionService.getCodigosByReporte(reporte)
                .then(res => {
                    this.listCodigos = res.data.codigos || []
                    this.listCodigosFiltrados = [...this.listCodigos]
                }).catch(err => console.error(err))

            ReporteLiquidacionService.getFilasByFormulario(this.selFormulario)
                .then(res => { this.listFilasExistentes = res.data.filas || [] })
                .catch(err => console.error(err))
        },

        seleccionarTipo(valor) {
            if (this.filaFormData.tipoFormulacion === valor) return
            this.filaFormData.tipoFormulacion = valor
            this.onTipoFormulacionChange()
        },

        onTipoFormulacionChange() {
            const d = this.filaFormData
            d.codigosSeleccionados = []
            d.operadoresCodigos = []
            d.filasSeleccionadas = []
            d.operadoresFilas = []
            d.codigosMunicipioSeleccionados = []
            d.operadoresCodigosMunicipio = []
            d.operandosPorcentaje = []
            d.comparacion = ''
            d.terminosComplejos = []
            d.filaBasePorcentaje = ''
            d.filaSumaPorcentaje = ''
            this.nuevoOperandoFila = ''
        },

        filtraCodigos() {
            if (!this.critCodigo) { this.listCodigosFiltrados = [...this.listCodigos]; return }
            const f = this.critCodigo.toUpperCase()
            this.listCodigosFiltrados = this.listCodigos.filter(c => String(c.codigo).toUpperCase().includes(f))
        },

        isCodigoSelected(c) { return this.filaFormData.codigosSeleccionados.includes(c.codigo) },
        toggleCodigoSelection(c) {
            const d = this.filaFormData
            const idx = d.codigosSeleccionados.indexOf(c.codigo)
            if (idx > -1) {
                d.codigosSeleccionados.splice(idx, 1)
                d.operadoresCodigos.splice(idx, 1)
            } else {
                d.codigosSeleccionados.push(c.codigo)
                d.operadoresCodigos.push('+')
            }
        },
        removeCodigoDeLista(idx) {
            this.filaFormData.codigosSeleccionados.splice(idx, 1)
            this.filaFormData.operadoresCodigos.splice(idx, 1)
        },

        getNombreCodigo(codigo) {
            const info = this.listCodigos.find(c => Number(c.codigo) === Number(codigo))
            return info && info.descripcion ? info.descripcion : codigo
        },

        isFilaSelected(f) { return this.filaFormData.filasSeleccionadas.includes(f.fila) },
        toggleFilaSelection(f) {
            const d = this.filaFormData
            const idx = d.filasSeleccionadas.indexOf(f.fila)
            if (idx > -1) {
                d.filasSeleccionadas.splice(idx, 1)
                d.operadoresFilas.splice(idx, 1)
            } else {
                d.filasSeleccionadas.push(f.fila)
                d.operadoresFilas.push('+')
            }
        },
        removeFilaDeLista(idx) {
            this.filaFormData.filasSeleccionadas.splice(idx, 1)
            this.filaFormData.operadoresFilas.splice(idx, 1)
        },

        isCodigoMunicipioSelected(c) { return this.filaFormData.codigosMunicipioSeleccionados.includes(c.codigo) },
        toggleCodigoMunicipioSelection(c) {
            const d = this.filaFormData
            const idx = d.codigosMunicipioSeleccionados.indexOf(c.codigo)
            if (idx > -1) {
                d.codigosMunicipioSeleccionados.splice(idx, 1)
                d.operadoresCodigosMunicipio.splice(idx, 1)
            } else {
                d.codigosMunicipioSeleccionados.push(c.codigo)
                d.operadoresCodigosMunicipio.push('+')
            }
        },
        removeCodigoMunicipioDeLista(idx) {
            this.filaFormData.codigosMunicipioSeleccionados.splice(idx, 1)
            this.filaFormData.operadoresCodigosMunicipio.splice(idx, 1)
        },

        addOperandoPorcentaje() {
            if (!this.nuevoOperandoFila) return
            this.filaFormData.operandosPorcentaje.push({
                operador: '+',
                fila: parseInt(this.nuevoOperandoFila),
                conPorcentaje: false
            })
            this.nuevoOperandoFila = ''
        },
        removeOperandoPorcentaje(idx) {
            this.filaFormData.operandosPorcentaje.splice(idx, 1)
        },

        addTerminoComplejo() { this.filaFormData.terminosComplejos.push({ signo: '+', fila: '' }) },
        removeTerminoComplejo(idx) { this.filaFormData.terminosComplejos.splice(idx, 1) },

        parseFormulacion(form) {
            if (!form) return { tipo: '' }
            if (form === 'manual') return { tipo: 'manual' }
            if (form === 'valor*tarifa') return { tipo: 'valorxtarifa' }

            if (/\*porcentaje/i.test(form)) {
                const operandos = this.parseExpresionPorcentajesLibre(form)
                if (operandos.length > 0) {
                    return { tipo: 'sumaporcentajes', operandosPorcentaje: operandos }
                }
            }

            let m = form.match(/^(sumacodigos|restemunicipio)\((.*)\)$/)
            if (m) {
                const tipo = m[1]
                const { valores, operadores } = this.parseExpresionOperadores(m[2])
                return { tipo, codigos: valores, operadoresCodigos: operadores }
            }

            m = form.match(/^sumamunicipio\((.*)\)$/)
            if (m) {
                const { valores, operadores } = this.parseExpresionOperadores(m[1])
                return { tipo: 'sumamunicipio', codigosMunicipio: valores, operadoresCodigosMunicipio: operadores }
            }

            m = form.match(/^sumaporcentajes\((.*)\)$/)
            if (m) {
                const operandosPorcentaje = this.parseExpresionPorcentajes(m[1])
                return { tipo: 'sumaporcentajes', operandosPorcentaje }
            }

            m = form.match(/^sumafilas\((.*)\)(?:([<>]0))?$/)
            if (m) {
                if (m[2]) {
                    return {
                        tipo: 'sumacompleja',
                        terminos: this.parseExpresionTerminos(m[1]),
                        comparacion: m[2]
                    }
                }
                const { valores, operadores } = this.parseExpresionOperadores(m[1])
                return { tipo: 'sumafilas', filas: valores, operadoresFilas: operadores }
            }

            m = form.match(/^sumafilas\(([\d-]+)\)$/)
            if (m) {
                const partes = m[1].split('-')
                return { tipo: 'sumafilas', filas: partes.map(p => parseInt(p)), operadoresFilas: partes.map((_, i) => i === 0 ? '+' : '-') }
            }

            return { tipo: '' }
        },

        parseExpresionOperadores(expr) {
            const valores = []
            const operadores = []
            const tokens = expr.match(/([+*/-]?)(\d+)/g) || []
            tokens.forEach((tok, i) => {
                const m = tok.match(/([+*/-]?)(\d+)/)
                if (m) {
                    const op = m[1] || '+'
                    operadores.push(i === 0 ? '+' : op)
                    valores.push(parseInt(m[2]))
                }
            })
            return { valores, operadores }
        },

        parseExpresionPorcentajes(expr) {
            const result = []
            const tokens = expr.match(/([+*/-]?)(\d+)(\*porcentaje)?/g) || []
            tokens.forEach((tok, i) => {
                const m = tok.match(/([+*/-]?)(\d+)(\*porcentaje)?/)
                if (m) {
                    const op = m[1] || '+'
                    const fila = parseInt(m[2])
                    const conPorc = !!m[3]
                    result.push({
                        operador: i === 0 ? '+' : op,
                        fila,
                        conPorcentaje: conPorc
                    })
                }
            })
            return result
        },

        parseExpresionPorcentajesLibre(expr) {
            const result = []
            const regex = /([+-]?)(sumafila|sumafilas)\(([\d,\-+]+)\)(\*porcentaje)?/gi
            let match
            let first = true
            while ((match = regex.exec(expr)) !== null) {
                const signo = match[1] || '+'
                const args = match[3]
                const conPorcentaje = !!match[4]

                const { valores, operadores } = this.parseExpresionOperadores(args)

                valores.forEach((fila, idx) => {
                    const op = idx === 0
                        ? (first ? '+' : signo)
                        : operadores[idx]
                    result.push({
                        operador: op,
                        fila,
                        conPorcentaje
                    })
                    first = false
                })
            }
            return result
        },

        parseExpresionTerminos(expr) {
            const terminos = []
            const regex = /([+-]?)(\d+)/g
            let match
            while ((match = regex.exec(expr)) !== null) {
                const signo = match[1] || '+'
                const fila = parseInt(match[2])
                if (!isNaN(fila)) terminos.push({ signo: signo === '' ? '+' : signo, fila })
            }
            return terminos
        },

        async guardarFila() {
            if (!this.isFilaValid) { this.touchedFila = true; this.$toast.warning('Complete los datos de la fila'); return }
            const userId = this.$store.state.userId || localStorage.getItem('userId')
            this.isLoading = true
            try {
                const payload = {
                    id: this.filaFormData.id || null,
                    formulario: parseInt(this.selFormulario),
                    grupo: this.filaFormData.grupo,
                    filaporciiu: this.filaFormData.filaporciiu ? 1 : 0,
                    fila: this.filaFormData.fila,
                    descripcion: this.filaFormData.descripcion,
                    formulacion: this.formulacionBd,
                    idUser: parseInt(userId)
                }
                if (this.isEditingFila) await ReporteLiquidacionService.updateFila(payload)
                else await ReporteLiquidacionService.createFila(payload)
                this.$toast.success(this.isEditingFila ? 'Fila actualizada' : 'Fila creada')
                this.cerrarModalFila()
                this.consultaFilas()
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Error al guardar')
            } finally {
                this.isLoading = false
            }
        },

        cerrarModalFila() {
            this.showModFila = false
            this.resetFilaFormData()
            this.touchedFila = false
        },

        deleteFila(item) {
            this.deleteId = item.id
            this.deleteTarget = 'fila'
            this.isDeleteAll = false
            this.showConfirmDeleteModal = true
        },
        borrarTodasFilas() {
            this.deleteId = null
            this.deleteTarget = 'fila'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },
        closeConfirmDeleteModal() {
            this.showConfirmDeleteModal = false
            this.deleteId = null
            this.deleteTarget = ''
            this.isDeleteAll = false
        },
        executeDelete() {
            if (this.deleteTarget === 'fila') {
                if (this.isDeleteAll) {
                    const promesas = this.listFilasExistentes.map(f => ReporteLiquidacionService.deleteFila(f.id))
                    Promise.all(promesas).then(() => { 
                        this.$toast.success('Todas las filas eliminadas'); 
                        this.closeConfirmDeleteModal(); 
                        this.consultaFilas() 
                    })
                } else {
                    ReporteLiquidacionService.deleteFila(this.deleteId)
                        .then(() => { this.$toast.success('Fila eliminada'); this.closeConfirmDeleteModal(); this.consultaFilas() })
                        .catch(() => { this.$toast.error('Error al eliminar') })
                }
            }
        }
    },
    mounted() { this.cargarFormularios() }
}
</script>

<style src="../main.css"></style>

<style scoped>
.niif-header .filters-container { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.niif-header .filter-select { min-width: 200px; max-width: 280px; width: auto; height: 38px; font-size: 14px; border-radius: 7px; }
.niif-header .consult-btn { height: 38px; min-height: 38px; padding: 0 20px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 14px; background-color: #6c757d !important; color: white !important; border: 1px solid #6c757d !important; cursor: pointer; flex-shrink: 0; }
.niif-header .consult-btn:disabled { opacity: 0.65; cursor: not-allowed; }

.page-back-btn { padding: 0; font-size: 1.5rem; line-height: 1; }
.back-icon { font-size: 1.75rem !important; width: 1.75rem !important; height: 1.75rem !important; }
.param-title { font-size: 2rem !important; font-weight: 500; }

.niif-toolbar-right { display: flex; align-items: center; justify-content: flex-end; gap: 8px; flex: 0 0 auto; }
.table-toolbar-add-btn { height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px; background-color: #28a745; color: white; border: 1px solid #28a745; }
.table-toolbar-add-btn:hover:not(:disabled) { background-color: #218838; border-color: #1e7e34; }
.table-toolbar-add-btn:disabled { opacity: 0.65; cursor: not-allowed; }

.uniform-modal-body { padding: 8px 4px 4px 4px; }
.edit-grupo-modal-body { max-height: 75vh; overflow-y: auto; }
.uniform-form-row { margin-bottom: 12px; }
.uniform-label-col label { margin-bottom: 0; font-weight: 500; font-size: 14px; }
.form-input { width: 100%; height: 38px; font-size: 14px; border-radius: 7px; }
.password-policy-box { background: white; border-radius: 8px; padding: 16px 20px; border-left: 4px solid #2e507a; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
.password-policy-title { font-weight: 600; font-size: 15px; color: #2e507a; margin-bottom: 4px; }
.password-policy-subtitle { font-size: 13px; color: #6c757d; }
.uniform-actions-wrap { display: flex; justify-content: flex-end; gap: 12px; margin-top: 20px; padding-top: 16px; border-top: 1px solid #e9ecef; flex-shrink: 0; }
.reset-btn-primary { background-color: #2e507a !important; border: 1px solid #2e507a !important; color: white !important; padding: 8px 24px; border-radius: 6px; font-weight: 500; transition: all 0.2s ease; }
.reset-btn-primary:hover:not(:disabled) { background-color: #1f3f62 !important; border-color: #1f3f62 !important; }
.reset-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
.reset-btn-secondary { background-color: #f8f9fa !important; border: 1px solid #ced4da !important; color: #495057 !important; padding: 8px 24px; border-radius: 6px; font-weight: 500; transition: all 0.2s ease; }
.reset-btn-secondary:hover:not(:disabled) { background-color: #e9ecef !important; border-color: #adb5bd !important; color: #495057 !important; }
.reset-btn-secondary:focus, .reset-btn-secondary:active { box-shadow: none !important; outline: none !important; }
.reset-btn-secondary:disabled { opacity: 0.65; cursor: not-allowed; }
.reset-btn-danger { background-color: #cd2838 !important; border: 1px solid #cd2838 !important; color: white !important; border-radius: 6px; }
.reset-modal-header { background-color: #2e507a; color: white; padding: 16px 20px; border-radius: 8px 8px 0 0; }
.reset-modal-title { font-size: 18px; font-weight: 600; margin: 0; }
.reset-modal-body { padding: 24px 20px; background-color: #f8f9fa; overflow: hidden; }

.danger-zone { border-top: 1px dashed #dee2e6; padding-top: 14px; }
.danger-zone-title { font-size: 13px; font-weight: 600; color: #cd2838; margin-bottom: 10px; display: flex; align-items: center; }
.danger-zone-row { padding: 10px 12px; background: #fff5f6; border: 1px solid #f5c2c7; border-radius: 8px; }
.danger-zone-label { font-size: 14px; font-weight: 600; color: #495057; }
.danger-zone-hint { font-size: 12px; color: #6c757d; }

.custom-scroll-table { height: 200px; min-height: 200px; max-height: 200px; overflow-y: auto; overflow-x: hidden; display: block; position: relative; border: 1px solid #dee2e6; border-radius: 8px; }
.custom-scroll-table table { display: table; width: 100%; table-layout: fixed; margin-bottom: 0; border-collapse: separate; border-spacing: 0; }
.custom-scroll-table table thead { display: table-header-group; position: sticky; top: 0; z-index: 10; }
.custom-scroll-table table thead th { background-color: #f8f9fa; border-bottom: 2px solid #dee2e6; padding: 6px 8px; font-weight: 600; font-size: 12px; color: #495057; position: sticky; top: 0; z-index: 10; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; }
.custom-scroll-table table thead th:first-child { width: 120px; }
.custom-scroll-table table tbody tr { cursor: pointer; }
.custom-scroll-table table tbody td { padding: 5px 8px; border: none; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; vertical-align: middle; }
.custom-scroll-table table tbody tr:hover td { background-color: #f5f9ff; }
.custom-scroll-table::-webkit-scrollbar { width: 8px; }
.custom-scroll-table::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
.custom-scroll-table::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.table-active-blue { background-color: #e3f0ff !important; }
.table-active-blue td { background-color: #e3f0ff !important; }

.formulacion-cell { font-family: inherit; font-size: 13px; word-break: break-word; max-width: 400px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

.operand-list { display: flex; flex-direction: column; gap: 6px; margin-top: 6px; }
.operand-row { display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: #fff; border: 1px solid #e9ecef; border-radius: 6px; }
.operand-index { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; background: #2e507a; color: #fff; border-radius: 50%; font-size: 11px; font-weight: 600; flex-shrink: 0; }
.operand-code { flex: 1; font-size: 13px; color: #495057; }

.custom-toggle-wrapper { display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0; }
.custom-toggle-wrapper input[type="checkbox"] { display: none; }
.custom-toggle-wrapper .toggle-label { display: flex; align-items: center; gap: 8px; cursor: pointer; user-select: none; margin: 0; }
.custom-toggle-wrapper .toggle-slider { position: relative; display: inline-block; width: 36px; height: 20px; background-color: #ccc; border-radius: 10px; transition: background-color 0.3s ease; flex-shrink: 0; box-shadow: inset 0 1px 2px rgba(0,0,0,0.15); }
.custom-toggle-wrapper .toggle-slider::after { content: ''; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; background-color: white; border-radius: 50%; transition: transform 0.3s ease; box-shadow: 0 1px 2px rgba(0,0,0,0.25); }
.custom-toggle-wrapper input[type="checkbox"]:checked + .toggle-label .toggle-slider { background-color: #2e507a; }
.custom-toggle-wrapper input[type="checkbox"]:checked + .toggle-label .toggle-slider::after { transform: translateX(16px); }
.custom-toggle-wrapper .toggle-text { font-size: 14px; font-weight: 500; color: #495057; }
.custom-toggle-wrapper.custom-toggle-sm .toggle-slider { width: 34px; height: 20px; }
.custom-toggle-wrapper.custom-toggle-sm .toggle-slider::after { width: 16px; height: 16px; }
.custom-toggle-wrapper.custom-toggle-sm input[type="checkbox"]:checked + .toggle-label .toggle-slider::after { transform: translateX(14px); }
.custom-toggle-wrapper.custom-toggle-sm .toggle-text { font-size: 12px; min-width: 48px; }

.modal-fixed-height .modal-dialog { max-height: 90vh; }
.modal-fixed-height .modal-content { max-height: 90vh; overflow: hidden; position: relative; }
.modal-fixed-height .modal-body { overflow: hidden; padding: 24px 20px; }

.uniform-modal-body--wide { padding: 8px 4px 4px 4px; height: 100%; display: flex; flex-direction: column; }

.menu-association-layout { display: flex; gap: 20px; height: 380px; min-height: 380px; max-height: 380px; background: white; border-radius: 8px; overflow: hidden; border: 1px solid #e9ecef; flex-shrink: 0; }
.menu-sidebar { flex: 0 0 220px; background: #f8f9fa; border-right: 1px solid #e9ecef; padding: 12px 0; overflow: hidden; display: flex; flex-direction: column; }
.menu-sidebar-title { font-size: 13px; font-weight: 600; color: #6c757d; text-transform: uppercase; letter-spacing: 0.5px; padding: 0 16px 10px 16px; border-bottom: 1px solid #e9ecef; margin-bottom: 6px; flex-shrink: 0; }
.menu-sidebar-list { flex: 1; overflow-y: auto; overflow-x: hidden; }
.menu-sidebar-item { display: block; width: 100%; text-align: left; padding: 10px 16px; background: transparent; border: none; border-radius: 0; color: #495057; font-size: 14px; font-weight: 500; transition: all 0.2s; cursor: pointer; }
.menu-sidebar-item:hover { background: #e9ecef; color: #2e507a; }
.menu-sidebar-item.active { background: #2e507a; color: white; }
.menu-sidebar-item.active:hover { background: #1f3f62; }

.menu-content { flex: 1; padding: 16px 20px; overflow: hidden; display: flex; flex-direction: column; min-width: 0; }
.menu-content-title { font-size: 16px; font-weight: 600; color: #2e507a; padding-bottom: 12px; border-bottom: 2px solid #f0f2f5; margin-bottom: 12px; flex-shrink: 0; }
.no-submenus { display: flex; align-items: center; justify-content: center; height: 100%; min-height: 100px; color: #6c757d; font-size: 14px; text-align: center; }

.menu-sidebar-list::-webkit-scrollbar, .fila-content-scroll::-webkit-scrollbar { width: 6px; }
.menu-sidebar-list::-webkit-scrollbar-track, .fila-content-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
.menu-sidebar-list::-webkit-scrollbar-thumb, .fila-content-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }
.menu-sidebar-list::-webkit-scrollbar-thumb:hover, .fila-content-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }

.fila-layout { height: 480px; min-height: 480px; max-height: 480px; }
.fila-layout .menu-sidebar { flex: 0 0 260px; }
.fila-layout .menu-sidebar-item { white-space: normal; font-size: 13px; line-height: 1.3; }

.fila-content-scroll { flex: 1; overflow-y: auto; overflow-x: hidden; padding-right: 4px; }

.fila-section { display: flex; gap: 16px; align-items: flex-start; flex-wrap: wrap; padding-bottom: 14px; margin-bottom: 14px; border-bottom: 1px solid #f0f2f5; }
.fila-field { display: flex; flex-direction: column; gap: 4px; min-width: 110px; }
.fila-field--grow { flex: 1; min-width: 200px; }
.fila-field .field-label { font-size: 13px; font-weight: 500; margin-bottom: 0; }

.fila-preview { flex-shrink: 0; margin-top: 12px; padding: 10px 14px; background: #f8f9fa; border-left: 4px solid #2e507a; border-radius: 6px; }
.fila-preview-label { font-size: 12px; color: #6c757d; margin-bottom: 2px; }
.fila-preview-code { font-family: monospace; font-size: 13px; color: #2e507a; word-break: break-all; }

@media (max-width: 992px) {
    .niif-header .filters-container { flex-wrap: wrap; width: 100%; margin-top: 8px; gap: 6px; }
    .niif-header .filter-select { min-width: 150px; max-width: 200px; flex: 1 1 auto; }
}
@media (max-width: 768px) {
    .modal-fixed-height .modal-body { padding: 16px 12px; }
    .menu-association-layout, .fila-layout { flex-direction: column; height: 560px; min-height: 560px; max-height: 560px; gap: 0; }
    .menu-sidebar { flex: 0 0 auto; border-right: none; border-bottom: 1px solid #e9ecef; max-height: 150px; padding: 8px 0; }
    .menu-sidebar-list { max-height: 100px; }
    .menu-sidebar-title { padding: 0 12px 8px 12px; font-size: 12px; }
    .menu-sidebar-item { padding: 8px 12px; font-size: 13px; }
    .menu-content { padding: 12px 16px; min-height: 200px; flex: 1; }
    .menu-content-title { font-size: 14px; padding-bottom: 8px; margin-bottom: 8px; }
    .custom-toggle-wrapper.custom-toggle-sm .toggle-slider { width: 30px; height: 18px; }
    .custom-toggle-wrapper.custom-toggle-sm .toggle-slider::after { width: 14px; height: 14px; top: 2px; left: 2px; }
    .custom-toggle-wrapper.custom-toggle-sm input[type="checkbox"]:checked + .toggle-label .toggle-slider::after { transform: translateX(12px); }
    .uniform-actions-wrap { flex-direction: column; margin-top: 12px; padding-top: 12px; }
    .uniform-actions-wrap .reset-btn-secondary { width: 100%; margin: 4px 0 !important; }
}
@media (max-width: 576px) {
    .param-title { font-size: 1.5rem !important; }
    .niif-header .filter-select { min-width: 100%; max-width: 100%; flex: 0 0 100%; }
    .custom-scroll-table { height: 150px; min-height: 150px; max-height: 150px; }
    .custom-scroll-table table thead th:first-child, .custom-scroll-table table tbody td:first-child { width: 70px; }
    .fila-layout { height: 480px; min-height: 480px; max-height: 480px; }
}
.btn-native-add {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #2e507a;
    color: white;
    border: 1px solid #2e507a;
    transition: all 0.2s ease;
}
.btn-native-add:hover:not(:disabled) {
    background-color: #1f3f62;
    border-color: #1f3f62;
    color: white;
}
.btn-native-add:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}
</style>